import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Globe,
  Map,
  Mountain,
  Compass,
  List,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { geographyCurriculum, geographySchemeOfWork } from '@/data/geographyCurriculum';
import { 
  year11GeographyUnitTests,
  year12GeographyUnitTests,
  year13GeographyUnitTests,
  year11GeographyExercises,
  year12GeographyExercises,
  year13GeographyExercises
} from '@/data/geographyUnitTests';
import { geographyWeeklyLessons } from '@/data/geographyWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { PenTool, Clock } from 'lucide-react';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function GeographyCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(c => c.year >= 11 && c.year <= 13 && 
            (c.subject?.toLowerCase().includes('geography') || 
             c.subject?.toLowerCase().includes('geo')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 11", "Year 12", "Year 13"]);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears(["Year 11", "Year 12", "Year 13"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const strandIcons = {
    0: Mountain,      // Natural Processes
    1: Globe,         // Cultural Processes
    2: Map,           // Local/Regional/Global Studies
    3: Compass        // Geographical Skills
  };

  const strandColors = ["green", "blue", "purple", "amber"];

  // Get curriculum data for selected year
  const getCurriculumData = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    return geographyCurriculum[yearKey] || geographyCurriculum.year11;
  };

  // Get scheme of work for selected year
  const getSchemeOfWork = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    return geographySchemeOfWork[yearKey] || geographySchemeOfWork.year11;
  };

  // Get unit tests for selected year
  const getUnitTests = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const testsMap = {
      'year11': year11GeographyUnitTests,
      'year12': year12GeographyUnitTests,
      'year13': year13GeographyUnitTests
    };
    return testsMap[yearKey] || year11GeographyUnitTests;
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getUnitTests();
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests?.[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  // Get exercises for selected year
  const getExercises = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const exercisesMap = {
      'year11': year11GeographyExercises,
      'year12': year12GeographyExercises,
      'year13': year13GeographyExercises
    };
    return exercisesMap[yearKey] || year11GeographyExercises;
  };

  const curriculumData = getCurriculumData();
  const schemeOfWork = getSchemeOfWork();
  const unitTests = getUnitTests();
  const exercises = getExercises();

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #166534; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #166534; background: #f0fdf4; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #166534; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Geography</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            <p>${section.questions} questions</p>
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printSchemeOfWork = () => {
    const printWindow = window.open('', '_blank');
    const termData = schemeOfWork[selectedTerm] || [];
    
    let content = `
      <html>
      <head>
        <title>Scheme of Work - ${selectedYear} Geography - ${selectedTerm.replace('term', 'Term ')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #166534; }
          h2 { color: #374151; border-bottom: 2px solid #166534; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #166534; color: white; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Scheme of Work</h1>
        <h2>${selectedYear} Geography - ${selectedTerm.replace('term', 'Term ')}</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Strand</th>
              <th>Topic</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            ${termData.map(week => `
              <tr>
                <td>${week.week}</td>
                <td>${week.strand}</td>
                <td>${week.topic}</td>
                <td>${week.activities}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-green-600 to-emerald-500 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Globe className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Geography Curriculum</CardTitle>
                <CardDescription className="text-green-100 mt-1">
                  Senior Secondary (Years 11-13) - Vanuatu National Syllabus
                </CardDescription>
              </div>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-green-100 text-sm">Year Level:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[140px] bg-white/20 border-white/30 text-white" data-testid="geography-year-selector">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {teacherYears.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Course Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Year</p>
                <p className="font-semibold">{curriculumData?.hoursPerYear || 132} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Strands</p>
                <p className="font-semibold">{curriculumData?.strands?.length || 4} Strands</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Assessment</p>
                <p className="font-semibold">
                  {selectedYear === "Year 11" ? "IE 60% / CA 40%" : "EE 60% / IA 40%"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Week</p>
                <p className="font-semibold">{curriculumData?.hoursPerWeek || 4} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 m-4">
            <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
              <BookOpen className="h-4 w-4" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="scheme" className="flex items-center gap-2" data-testid="scheme-tab">
              <ClipboardList className="h-4 w-4" />
              Scheme
            </TabsTrigger>
            <TabsTrigger value="tests" className="flex items-center gap-2" data-testid="tests-tab">
              <ClipboardCheck className="h-4 w-4" />
              Tests
            </TabsTrigger>
            <TabsTrigger value="exercises" className="flex items-center gap-2" data-testid="exercises-tab">
              <FileText className="h-4 w-4" />
              Exercises
            </TabsTrigger>
            <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
              <FileQuestion className="h-4 w-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
              <Files className="h-4 w-4" />
              Quick Print
            </TabsTrigger>
            <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-green-100" data-testid="lesson-plans-tab">
              <PenTool className="h-4 w-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
              <Sparkles className="h-4 w-4" />
              AI Generator
            </TabsTrigger>
          </TabsList>

          {/* Term Exam Tab */}
          <TabsContent value="term-exam" className="p-4">
            <TermExamGenerator
              subject="Geography"
              year={parseInt(selectedYear.replace('Year ', ''))}
              unitTests={getUnitTestsForYear()}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Quick Print Tab */}
          <TabsContent value="quick-print" className="p-4">
            <QuickPrintAll
              subject="Geography"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={geographyWeeklyLessons}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Lesson Plans Tab - Full Week Mon-Fri */}
          <TabsContent value="lesson-plans" className="p-4">
            <LessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              subject="Geography"
              subjectColor="teal"
              allowedYears={['11', '12', '13']}
              weeklyLessons={geographyWeeklyLessons}
            />
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">{curriculumData?.title || `${selectedYear} Geography`}</h3>
              <p className="text-gray-500">{curriculumData?.description || 'Understanding our world through physical and human geography'}</p>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {curriculumData?.strands?.map((strand, index) => {
                const StrandIcon = strandIcons[index] || Globe;
                return (
                  <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg overflow-hidden">
                    <AccordionTrigger className={`px-4 py-3 bg-${strandColors[index]}-50 hover:bg-${strandColors[index]}-100`}>
                      <div className="flex items-center gap-3">
                        <StrandIcon className={`h-5 w-5 text-${strandColors[index]}-600`} />
                        <Badge className={`bg-${strandColors[index]}-500 text-white`}>
                          Strand {index + 1}
                        </Badge>
                        <span className="font-semibold">{strand.name}</span>
                        <span className="text-gray-500 text-sm ml-2">({strand.hours} hours)</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4 bg-white">
                      <div className="space-y-3">
                        {strand.topics?.map((topic, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                            <div>
                              <p className="font-medium">{topic.name}</p>
                              <p className="text-sm text-gray-500">{topic.description}</p>
                              {topic.weeks && <Badge variant="outline" className="mt-1">Weeks {topic.weeks}</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </TabsContent>

          {/* Scheme of Work Tab */}
          <TabsContent value="scheme" className="p-4">
            <SubjectSchemeOfWork
              subject="Geography"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={geographyWeeklyLessons}
              subjectColor="teal"
            />
          </TabsContent>

          {/* Unit Tests Tab */}
          <TabsContent value="tests" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Unit Tests - {selectedYear}</h3>
              <p className="text-gray-500">Practice tests and assessments for each term</p>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {Object.entries(unitTests).map(([termKey, termData]) => (
                <AccordionItem key={termKey} value={termKey} className="border rounded-lg">
                  <AccordionTrigger className="px-4 py-3 bg-green-50 hover:bg-green-100">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-green-500 text-white">
                        {termData.title}
                      </Badge>
                      <span className="text-gray-600">
                        {termData.tests?.length || 0} Tests
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4">
                    <div className="space-y-3">
                      {termData.tests?.map((test, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <ClipboardCheck className="h-5 w-5 text-green-500" />
                            <div>
                              <p className="font-medium">{test.title}</p>
                              <p className="text-sm text-gray-500">
                                {test.duration} | {test.totalMarks} marks
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => printTest(test)}
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </Button>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Exercises - {selectedYear}</h3>
              <p className="text-gray-500">Practice worksheets and activities</p>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {Object.entries(exercises).map(([unitKey, unitData]) => (
                <AccordionItem key={unitKey} value={unitKey} className="border rounded-lg">
                  <AccordionTrigger className="px-4 py-3 bg-blue-50 hover:bg-blue-100">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">{unitData.title}</span>
                      <Badge variant="outline">
                        {unitData.exercises?.length || 0} exercises
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4">
                    <div className="space-y-3">
                      {unitData.exercises?.map((exercise, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium">{exercise.title}</p>
                            <Badge>{exercise.type}</Badge>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{exercise.instructions}</p>
                          {exercise.questions?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {exercise.questions.map((q, qIdx) => (
                                <p key={qIdx} className="text-sm text-gray-600">• {q}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>

          {/* AI Lesson Plan Tab */}
          <TabsContent value="ai-lesson" className="p-4">
            <AILessonPlanGenerator 
              subject="Geography"
              yearLevel={selectedYear}
              strands={curriculumData?.strands?.map(s => s.name) || []}
            />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
